import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const initialState = {
  // Auth state
  isLoggedIn: false,
  userID: null,
  userName: null,
  employeeId: null,
  firstName: null,
  lastName: null,
  middleName: null,
  imgUrl: null,
  admin: false,
  roleName: null,
  roleId: null,
  locationId: null,
  locationName: null,

  // Global Sales Order Config
  withSalesQuotation: true, // Default to true

  // Price Display Config — driven by PersonalizedPricing API
  // Defaults to all true so nothing is hidden before config loads
  priceTypeConfig: {
    showVatInc: true,
    showVatEx: true,
    showReseller: true,
    showZeroRated: true,
    configName: null,
  },

  // Other application state
  posProducts: [],
  checkedBrands: [],
  checkedCategories: [],
  checkedCategoriesTwo: [],
  checkedCategoriesThree: [],
  checkedCategoriesFour: [],
  checkedCategoriesFive: [],
  lastModifiedProduct: null,
  lastModifiedProducts: [],
  selectedCustomer: null,
  refreshProducts: false,
  existingLocation: null,
};

// 🌟 HELPER: Calculate total quantity currently in cart for a specific product ID (in Base Units)
// This now correctly scans both regular products AND components inside BOM products
export const calculateTotalBaseUsage = (cartItems, targetProductId) => {
  return cartItems.reduce((total, item) => {
    let usage = 0;

    // 1. Direct usage (e.g., Adding a Gallon of Water directly)
    if (item.productId === targetProductId) {
      const rate = item.conversionRate || 1;
      usage += item.quantity * rate;
    }

    // 2. Indirect usage (e.g., Mango Shake consuming Water as a BOM component)
    if (item.isBom && Array.isArray(item.bomComponents)) {
      const component = item.bomComponents.find(
        (c) => c.componentProductId === targetProductId,
      );
      if (component) {
        // Backend already calculates how many base units are needed per 1 selling unit
        usage += item.quantity * component.requiredBaseQuantity;
      }
    }

    return total + usage;
  }, 0);
};

export const IchthusSlice = createSlice({
  name: "Ichthus",
  initialState,
  reducers: {
    // --- AUTH REDUCERS ---
    SET_ACTIVE_USER: (state, action) => {
      const {
        userID,
        userName,
        employeeId,
        imgUrl,
        admin,
        roleName,
        roleId,
        firstName,
        lastName,
        middleName,
        locationId,
        locationName,
      } = action.payload;

      state.isLoggedIn = true;
      state.userID = userID;
      state.userName = userName;
      state.employeeId = employeeId;
      state.imgUrl = imgUrl;
      state.admin = admin;
      state.roleName = roleName;
      state.roleId = roleId;
      state.firstName = firstName;
      state.lastName = lastName;
      state.middleName = middleName;
      state.locationId = locationId;
      state.locationName = locationName;
    },

    REMOVE_ACTIVE_USER: (state) => {
      state.isLoggedIn = false;
      state.userID = null;
      state.userName = null;
      state.employeeId = null;
      state.imgUrl = null;
      state.admin = false;
      state.roleName = null;
      state.roleId = null;
      state.firstName = null;
      state.lastName = null;
      state.middleName = null;
      state.locationId = null;
      state.locationName = null;
      state.posProducts = [];
      state.lastModifiedProducts = [];
      state.selectedCustomer = null;
      // Reset configs on logout
      state.priceTypeConfig = initialState.priceTypeConfig;
      state.withSalesQuotation = initialState.withSalesQuotation;
    },

    // --- GLOBAL SALES ORDER CONFIG REDUCER ---
    SET_SalesOrderConfig: (state, action) => {
      state.withSalesQuotation = action.payload;
    },

    // --- PRICE TYPE CONFIG REDUCER ---
    SET_PriceTypeConfig: (state, action) => {
      const { showVatInc, showVatEx, showReseller, showZeroRated, configName } =
        action.payload;
      state.priceTypeConfig = {
        showVatInc: showVatInc ?? true,
        showVatEx: showVatEx ?? true,
        showReseller: showReseller ?? true,
        showZeroRated: showZeroRated ?? true,
        configName: configName || null,
      };
    },

    // --- POS REDUCERS ---
    addToPos: (state, action) => {
      const incoming = action.payload;
      const incomingRate = incoming.conversionRate || 1;
      const incomingBaseQty = (incoming.quantity || 1) * incomingRate;
      const maxBaseStock = incoming.maxQuantity * incomingRate;
      const currentBaseUsage = calculateTotalBaseUsage(
        state.posProducts,
        incoming.productId,
      );

      if ((incoming.quantity || 1) < 1) {
        toast.error("Quantity must be at least 1.");
        return;
      }

      if (currentBaseUsage + incomingBaseQty > maxBaseStock) {
        toast.error(
          `Insufficient stock! You have ${Math.floor(
            (maxBaseStock - currentBaseUsage) / incomingRate,
          )} units remaining.`,
        );
        return;
      }

      const itemIndex = state.posProducts.findIndex(
        (item) => item.id === incoming.id,
      );

      if (itemIndex >= 0) {
        state.posProducts[itemIndex].quantity += incoming.quantity || 1;
      } else {
        state.posProducts.push({
          ...incoming,
          quantity: incoming.quantity || 1,
          isBom: incoming.isBom || false,
          bomComponents: incoming.bomComponents || [], // 🌟 Save BOM components to cart
        });
      }

      const existingLastModified = state.lastModifiedProducts.find(
        (p) => p.id === incoming.id,
      );

      const updatedItemQty =
        itemIndex >= 0
          ? state.posProducts[itemIndex].quantity
          : incoming.quantity || 1;

      if (existingLastModified) {
        existingLastModified.quantity = updatedItemQty;
      } else {
        state.lastModifiedProducts.push({
          id: incoming.id,
          quantity: updatedItemQty,
        });
      }

      state.lastModifiedProduct = {
        id: incoming.id,
        quantity: updatedItemQty,
      };

      toast.success("Product added to POS");
    },

    setExistingLocation: (state, action) => {
      state.existingLocation = action.payload;
    },

    increasePosQuantity: (state, action) => {
      const item = state.posProducts.find(
        (item) => item.id === action.payload.id,
      );

      if (item) {
        const rate = item.conversionRate || 1;
        const oneUnitInBase = 1 * rate;
        const maxBaseStock = item.maxQuantity * rate;
        const currentBaseUsage = calculateTotalBaseUsage(
          state.posProducts,
          item.productId,
        );

        if (currentBaseUsage + oneUnitInBase > maxBaseStock) {
          toast.error(
            `Cannot increase quantity. Exceeds total available stock.`,
          );
          return;
        }

        item.quantity++;

        const existingLastModified = state.lastModifiedProducts.find(
          (p) => p.id === item.id,
        );
        if (existingLastModified) {
          existingLastModified.quantity = item.quantity;
        } else {
          state.lastModifiedProducts.push({
            id: item.id,
            quantity: item.quantity,
          });
        }
        state.lastModifiedProduct = { id: item.id, quantity: item.quantity };
      }
    },

    decreasePosQuantity: (state, action) => {
      const item = state.posProducts.find(
        (item) => item.id === action.payload.id,
      );
      if (item && item.quantity > 1) {
        item.quantity--;

        const existingLastModified = state.lastModifiedProducts.find(
          (p) => p.id === item.id,
        );
        if (existingLastModified) {
          existingLastModified.quantity = item.quantity;
        } else {
          state.lastModifiedProducts.push({
            id: item.id,
            quantity: item.quantity,
          });
        }
        state.lastModifiedProduct = { id: item.id, quantity: item.quantity };
      }
    },

    updateQuantity: (state, action) => {
      const { id, quantity } = action.payload;
      const product = state.posProducts.find((item) => item.id === id);

      if (product) {
        if (quantity < 1) {
          toast.error("Quantity must be at least 1.");
          return;
        }

        const rate = product.conversionRate || 1;
        const maxBaseStock = product.maxQuantity * rate;

        // 🌟 Uses the unified base usage to check stock limits accurately
        const currentBaseUsage = calculateTotalBaseUsage(
          state.posProducts,
          product.productId,
        );
        const currentItemBaseQty = product.quantity * rate;
        const usageWithoutCurrent = currentBaseUsage - currentItemBaseQty;
        const newProposedUsage = quantity * rate;

        if (usageWithoutCurrent + newProposedUsage > maxBaseStock) {
          toast.error(`Total quantity exceeds available stock.`);
          return;
        }

        product.quantity = quantity;

        const existingLastModified = state.lastModifiedProducts.find(
          (p) => p.id === id,
        );
        if (existingLastModified) {
          existingLastModified.quantity = product.quantity;
        } else {
          state.lastModifiedProducts.push({ id, quantity: product.quantity });
        }
        state.lastModifiedProduct = { id, quantity: product.quantity };
      }
    },

    updateDiscount: (state, action) => {
      const { id, discount } = action.payload;
      const product = state.posProducts.find((item) => item.id === id);
      if (product) {
        const maxDiscount = product.quantity * product.price;
        product.discount = Math.max(0, Math.min(discount, maxDiscount));
      }
    },

    deleteItemPos: (state, action) => {
      const deletedProduct = state.posProducts.find(
        (item) => item.id === action.payload,
      );

      state.posProducts = state.posProducts.filter(
        (item) => item.id !== action.payload,
      );

      if (deletedProduct) {
        state.lastModifiedProducts = state.lastModifiedProducts.filter(
          (item) => item.id !== deletedProduct.id,
        );

        const newLastModifiedProduct = {
          id: deletedProduct.id,
          quantity: 0,
        };
        state.lastModifiedProduct = newLastModifiedProduct;
        state.lastModifiedProducts.push(newLastModifiedProduct);
      }
      toast.error("Product removed from POS");
    },

    toggleBrand: (state, action) => {
      const brand = action.payload;
      const isBrandChecked = state.checkedBrands.some((b) => b.id === brand.id);
      if (isBrandChecked) {
        state.checkedBrands = state.checkedBrands.filter(
          (b) => b.id !== brand.id,
        );
      } else {
        state.checkedBrands.push(brand);
      }
    },
    toggleCategory: (state, action) => {
      const category = action.payload;
      const isCategoryChecked = state.checkedCategories.some(
        (b) => b.id === category.id,
      );
      if (isCategoryChecked) {
        state.checkedCategories = state.checkedCategories.filter(
          (b) => b.id !== category.id,
        );
      } else {
        state.checkedCategories.push(category);
      }
    },
    toggleCategoryTwo: (state, action) => {
      const category = action.payload;
      const isChecked = state.checkedCategoriesTwo.some(
        (b) => b.id === category.id,
      );
      if (isChecked)
        state.checkedCategoriesTwo = state.checkedCategoriesTwo.filter(
          (b) => b.id !== category.id,
        );
      else state.checkedCategoriesTwo.push(category);
    },
    toggleCategoryThree: (state, action) => {
      const category = action.payload;
      const isChecked = state.checkedCategoriesThree.some(
        (b) => b.id === category.id,
      );
      if (isChecked)
        state.checkedCategoriesThree = state.checkedCategoriesThree.filter(
          (b) => b.id !== category.id,
        );
      else state.checkedCategoriesThree.push(category);
    },
    toggleCategoryFour: (state, action) => {
      const category = action.payload;
      const isChecked = state.checkedCategoriesFour.some(
        (b) => b.id === category.id,
      );
      if (isChecked)
        state.checkedCategoriesFour = state.checkedCategoriesFour.filter(
          (b) => b.id !== category.id,
        );
      else state.checkedCategoriesFour.push(category);
    },
    toggleCategoryFive: (state, action) => {
      const category = action.payload;
      const isChecked = state.checkedCategoriesFive.some(
        (b) => b.id === category.id,
      );
      if (isChecked)
        state.checkedCategoriesFive = state.checkedCategoriesFive.filter(
          (b) => b.id !== category.id,
        );
      else state.checkedCategoriesFive.push(category);
    },
    resetPos: (state) => {
      state.posProducts = [];
      state.lastModifiedProduct = null;
      state.lastModifiedProducts = [];
      toast.info("POS has been reset");
    },
    setSelectedCustomer: (state, action) => {
      state.selectedCustomer = action.payload;
    },
    clearSelectedCustomer: (state) => {
      state.selectedCustomer = null;
    },
    triggerRefresh: (state) => {
      state.refreshProducts = !state.refreshProducts;
    },
  },
});

export const {
  SET_ACTIVE_USER,
  REMOVE_ACTIVE_USER,
  SET_PriceTypeConfig,
  SET_SalesOrderConfig,
  addToPos,
  resetPos,
  increasePosQuantity,
  decreasePosQuantity,
  deleteItemPos,
  toggleBrand,
  toggleCategory,
  toggleCategoryTwo,
  toggleCategoryThree,
  toggleCategoryFour,
  toggleCategoryFive,
  updateDiscount,
  updateQuantity,
  setSelectedCustomer,
  clearSelectedCustomer,
  triggerRefresh,
  setExistingLocation,
} = IchthusSlice.actions;

// Selectors
export const selectIsLoggedIn = (state) => state.orebiReducer.isLoggedIn;
export const selectUserID = (state) => state.orebiReducer.userID;
export const selectUserName = (state) => state.orebiReducer.userName;
export const selectEmployeeId = (state) => state.orebiReducer.employeeId;
export const selectImgUrl = (state) => state.orebiReducer.imgUrl;
export const selectAdmin = (state) => state.orebiReducer.admin;
export const selectRoleName = (state) => state.orebiReducer.roleName;
export const selectRoleId = (state) => state.orebiReducer.roleId;
export const selectFirstName = (state) => state.orebiReducer.firstName;
export const selectLastName = (state) => state.orebiReducer.lastName;
export const selectFullName = (state) =>
  `${state.orebiReducer.firstName || ""} ${
    state.orebiReducer.lastName || ""
  }`.trim();
export const selectLocationId = (state) => state.orebiReducer.locationId;
export const selectLocationName = (state) => state.orebiReducer.locationName;
export const selectPosProducts = (state) => state.orebiReducer.posProducts;
export const selectLastModifiedProduct = (state) =>
  state.orebiReducer.lastModifiedProduct;
export const selectLastModifiedProducts = (state) =>
  state.orebiReducer.lastModifiedProducts;
export const selectSelectedCustomer = (state) =>
  state.orebiReducer.selectedCustomer;
export const selectPriceTypeConfig = (state) =>
  state.orebiReducer.priceTypeConfig;

// NEW: Sales Order Config Selector
export const selectWithSalesQuotation = (state) =>
  state.orebiReducer.withSalesQuotation;

export default IchthusSlice.reducer;
